import { FC, PropsWithChildren, ReactElement, useEffect } from 'react';
import { FormState } from 'react-hook-form';
import useAlertProvider from '../alert/useAlertProvider';
import { navigateBackToCallingApp } from '../../lib/utils/utils';
import { clickAnalytics } from '../../lib/utils/analytics';
import { Button, Link } from '@digital-pulse/react';
import styles from './form.module.scss';
import { displayAlertMessage, DOB_ERROR_MSG } from '@/lib/constants/dateOfBirth';

interface FormProps {
  errorTitle?: string;
  formState: FormState<any>;
  onSubmit: () => void;
  onCancel?: () => void;
  submitBtnText?: string;
  cancelLinkText?: string;
  handleModal?: () => void;
  handleMinorModal?: () => void;
  cancelTag?: string;
  isUnifiedRegistration?: boolean;
  onlyDOBFieldFlag?: boolean;
  dobErrorMessages?: any;
}

const Form: FC<PropsWithChildren<FormProps>> = ({
  errorTitle,
  formState,
  onSubmit,
  onCancel,
  children,
  submitBtnText = 'Search',
  cancelLinkText = 'Cancel',
  handleModal,
  handleMinorModal,
  cancelTag,
  isUnifiedRegistration = false,
  onlyDOBFieldFlag = false,
  dobErrorMessages = DOB_ERROR_MSG
}): ReactElement => {
  const { setAlerts } = useAlertProvider();

  const handleCancel = (tag: string) => {
    handleModal && handleModal();
    onCancel && onCancel();
    if (!onCancel) {
      const data = {
        payload: {
          link_name: tag
        }
      };
      clickAnalytics(data);
      setTimeout(() => {
        navigateBackToCallingApp('cancel');
      }, 100);
    }
  };

  const setAlertErrorMessage = (errObj: any) => {
    const elemName = errObj?.ref?.name;

    if (elemName === 'dateOfBirth') {
      return displayAlertMessage(errObj, dobErrorMessages);
    }

    return errObj?.message?.toString();
  };

  useEffect(() => {
    if (formState.isSubmitSuccessful) {
      return;
    }
    if (
      Object.keys(formState.errors).length > 0 &&
      formState?.errors?.dateOfBirth?.type === 'minor'
    ) {
      handleMinorModal();
    }
    setAlerts(
      Object.keys(formState.errors).length === 0
        ? []
        : [
            {
              title: errorTitle,
              actions: (
                <ul key={Object.keys(formState.errors).toString()} role="list">
                  {Object.keys(formState.errors).map((error) => (
                    <li key={error + formState.errors[error]?.type} role="listitem">
                      <Link
                        href="javascript:;"
                        variant="inline"
                        onClick={(ev) => {
                          ev.preventDefault();
                          // Special handling for dateOfBirth - focus on the single DOB input
                          if (error === 'dateOfBirth') {
                            const dobInput = document.getElementById('dateOfBirth') || document.querySelector('input[name="dateOfBirth"]');
                            dobInput?.focus?.();
                          } else {
                            const errEl = formState.errors[error]?.ref as HTMLElement;
                            errEl?.focus?.();
                          }
                        }}
                        subtype="high-contrast"
                      >
                        {setAlertErrorMessage(formState.errors[error])}
                      </Link>
                    </li>
                  ))}
                </ul>
              )
            }
          ]
    );
  }, [
    errorTitle,
    formState.errors,
    formState.submitCount,
    formState.isSubmitSuccessful,
    setAlerts
  ]);

  return (
    <div className={!isUnifiedRegistration && styles.container}>
      <div className={styles.form_container}>{children}</div>
      <div
        className={isUnifiedRegistration ? styles.guestButton : styles.button_container}
        id="button_container"
      >
        <Button className={styles.button} isFullWidth={!isUnifiedRegistration} onClick={onSubmit}>
          {submitBtnText}
        </Button>
        {!onlyDOBFieldFlag && !isUnifiedRegistration && (
          <Link onClick={() => handleCancel(cancelTag)} href="javascript:;" subtype="high-contrast">
            {cancelLinkText}
          </Link>
        )}
      </div>
    </div>
  );
};

export default Form;
